import React, { useEffect, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { OnDocumentLoadSuccess } from "react-pdf/dist/esm/shared/types.js";

import { getTranslateByKey } from "@/main/services/utils";

import Button from "../button/button";
import SecondaryButton from "../button/secondaryButton";
import LoadingComponent from "../loading/loadingComponent";
import * as S from "./styles";
import pdfWorker from "pdfjs-dist/build/pdf.worker.min.mjs?url";

pdfjs.GlobalWorkerOptions.workerSrc = pdfWorker;

interface IWrapperButtonsProps {
	containerRef: React.RefObject<HTMLDivElement | null>;
	totalPages: number;
	allPagesRendered: boolean;
	isLoading: boolean;
	onAccept: () => void;
	onDecline: () => void;
}

const WrapperButtons = ({
	containerRef,
	totalPages,
	allPagesRendered,
	isLoading,
	onAccept,
	onDecline,
}: IWrapperButtonsProps) => {
	const [readComplete, setReadComplete] = useState(false);

	function checkScrollCompletion() {
		if (containerRef.current) {
			const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
			if (scrollHeight <= clientHeight || scrollTop + clientHeight >= scrollHeight - 50) {
				setReadComplete(true);
			}
		}
	}

	useEffect(() => {
		const containerElement = containerRef.current;
		if (!containerElement) return;
		containerElement.addEventListener("scroll", checkScrollCompletion);
		return () => containerElement.removeEventListener("scroll", checkScrollCompletion);
	}, []);

	useEffect(() => {
		if (allPagesRendered) {
			checkScrollCompletion();
		}
	}, [allPagesRendered]);

	const showButton = totalPages > 0 && readComplete;

	return (
		<S.AreaButton>
			<SecondaryButton id="id-eula-button-decline" onClick={onDecline}>
				{getTranslateByKey("information.decline")}
			</SecondaryButton>
			<Button id="id-eula-button-accept" onClick={onAccept} disabled={!showButton || isLoading} isLoading={isLoading}>
				{getTranslateByKey("information.accept")}
			</Button>
		</S.AreaButton>
	);
};

interface PDFViewerProps {
	urlFile: string;
	isLoading: boolean;
	onAccept: () => void;
	onDecline: () => void;
}

function PDFViewer({ urlFile, isLoading, onAccept, onDecline }: PDFViewerProps) {
	const containerRef = useRef<HTMLDivElement>(null);
	const [totalPages, setTotalPages] = useState(0);
	const [renderedPages, setRenderedPages] = useState(0);

	const allPagesRendered = totalPages > 0 && renderedPages >= totalPages;

	const handleDocumentLoadSuccess: OnDocumentLoadSuccess = ({ numPages }) => {
		setTotalPages(numPages);
	};

	return (
		<S.Container>
			{urlFile === "" ? (
				<LoadingComponent />
			) : (
				<>
					<S.PDFContainer ref={containerRef} id="id-eula-ref-pdfviewer">
						<Document
							file={`data:application/pdf;base64,${urlFile}`}
							onLoadSuccess={handleDocumentLoadSuccess}
							loading={<LoadingComponent />}
							error={<h5>{getTranslateByKey("information.failedLoadFile", true)}</h5>}
						>
							{Array.from(new Array(totalPages), (el, index) => (
								<Page
									key={`page_${index + 1}`}
									pageNumber={index + 1}
									renderTextLayer={false}
									renderAnnotationLayer={false}
									className="pdf-page"
									onRenderSuccess={() => setRenderedPages((prev) => prev + 1)}
								/>
							))}
						</Document>
					</S.PDFContainer>
					<WrapperButtons
						containerRef={containerRef}
						totalPages={totalPages}
						allPagesRendered={allPagesRendered}
						isLoading={isLoading}
						onAccept={onAccept}
						onDecline={onDecline}
					/>
				</>
			)}
		</S.Container>
	);
}

export default PDFViewer;
